import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  getIncomingGaps,
  listDuplicates,
  listIncomingDocuments,
  ladeUebernahmeAngelegt,
  listKontierungenWartend,
  zaehlePortalVorgaengeOffen,
  zaehleMailKontenMitFehler,
  zaehleWebhookErschoepft,
} from '../api/client';
import type { Me } from '../api/schemas';
import { LizenzHinweis } from '../lizenz/LizenzHinweis';
import { useFarbschema } from './farbschema';
import { Kopfzeile } from './Kopfzeile';
import {
  alleKategorieSchluessel,
  brotkrumen,
  findeEintrag,
  hauptaktionFuer,
  sectionOf,
  sichtbareEintraege,
  type AbzeichenQuelle,
  type Abzeichenstand,
  type NavBedingung,
  type NavFunktion,
} from './navigation';
import { useGuthabenAktiv } from '../api/funktionen';
import { useFirmenname } from './marke';
import { Sidebar } from './Sidebar';
import { useSeitenspalte } from './seitenspalte-zustand';
import type { Navigation } from './router';

/**
 * Der Rahmen: Menüspalte links, klebende Kopfzeile oben, Arbeitsfläche darunter.
 *
 * **Diese Datei hält nur noch zusammen.** Die Gliederung steht als Daten in
 * `navigation.ts`, die Spalte in `Sidebar.tsx`, die Kopfzeile in
 * `Kopfzeile.tsx`, der gemerkte Klappzustand in `seitenspalte-zustand.ts`. Vor
 * dem Umbau lag alles vier in einer Datei; die Gliederung war damit an eine
 * Bauteildatei gebunden, und ihr zweiter Leser — die Brotkrumen — hätte eine
 * zweite Tabelle gebraucht.
 *
 * **Die Navigation richtet sich nach den Fähigkeiten aus `/auth/me`.** Wer
 * `journal:read` nicht hat, sieht den Punkt nicht — und bekäme, wenn er die
 * Adresse tippte, trotzdem eine 403 vom Server. Das Ausblenden ist
 * Bequemlichkeit und ausdrücklich kein Schutz.
 *
 * **Die Benutzerverwaltung hat seit `W-mitarbeiteranlage` eine eigene Seite**
 * (`users/UsersPage.tsx`, Menüpunkt «Benutzer», `user:read`). Bis dahin fehlte
 * sie mit Absicht — Konten entstanden nur über `POST /api/v1/users`, von Hand.
 *
 * ## Warum die Arbeitsfläche rollt und nicht das Fenster
 *
 * Ab `lg` ist der Rahmen ein Gitter fester Höhe: Die Spalte ist einen
 * Bildschirm hoch, die Kopfzeile ist ein Geschwister der Arbeitsfläche, und die
 * Arbeitsfläche bekommt ihre eigene Rollfläche (`overflow-y-auto`).
 *
 * Das ist die Antwort auf die Falle, wegen der die Kopfzeile bisher NICHT
 * klebte: Eine klebende Kopfzeile über einem rollenden Dokument schiebt sich
 * beim Anspringen eines Elements darüber, und Playwright bricht mit «element
 * intercepts pointer events» ab — an einer Stelle, die mit der Kopfzeile nichts
 * zu tun hat. Liegt die Kopfzeile dagegen NEBEN der Rollfläche statt darüber,
 * kann kein Element unter sie geraten; das Übergabepaket bekommt seine immer
 * sichtbare Kopfzeile, und der Prüflauf behält seine Klickbarkeit.
 *
 * Unterhalb von `lg` bleibt alles beim gewöhnlichen Dokumentfluss: Die
 * Menüspalte ist ein Block über dem Inhalt, das Fenster rollt, und `sticky
 * top-0` an der Kopfzeile ist dort die einzige Wirkung.
 *
 * **Die Wurzel ist `relative`, und das ist die Zusage gegen das endlose Rollen
 * (`W-auswertungscroll`, `E-2123`).** `overflow: hidden` schneidet nur
 * Nachfahren ab, deren Bezugsrahmen innerhalb des Rahmens liegt. Eine
 * `position: absolute`-Zeile ohne eigenen Bezugsrahmen hängt dagegen am
 * Dokument und entkommt dem Rahmen — und jede `sr-only`-Liste und jede
 * `<caption class="sr-only">` der Chartbausteine ist genau so eine Zeile. Sie
 * steht an der Stelle, an der sie im Fluss stünde, also tief in der Rollfläche
 * (gemessen: 3.274 px bei 900 px Fensterhöhe), und gibt dem Dokument eine
 * Rollhöhe, die der Rahmen nie zeigt. Der Anwender fand unter dem letzten Absatz
 * der Auswertung leere Fläche, und die Bildlaufleiste lief weit über den
 * Inhalt hinaus. Mit `relative` ist die Wurzel der Bezugsrahmen aller
 * absolut gesetzten Nachfahren der Schale: Der Rahmen schneidet sie ab, und das
 * Dokument bleibt so hoch wie das Fenster. Unterhalb von `lg`, wo die Schale
 * mit dem Inhalt wächst, ändert sich nichts — dort liegen sie ohnehin im Fluss.
 */
export function Shell(props: {
  readonly user: Me;
  readonly navigation: Navigation;
  readonly onSignOut: () => void;
  readonly children: ReactNode;
}): ReactNode {
  const route = props.navigation.route;
  const aktiverPunkt = sectionOf(route);
  const capabilities = useMemo(
    () => new Set(props.user.capabilities),
    [props.user.capabilities],
  );
  const farbschema = useFarbschema();

  const bedingungen = useNavBedingungen(capabilities, route.name === 'einrichtung');
  // Ausgeschaltete Funktionen (`D-57`, `E-2078`): heute nur das Kundenguthaben.
  const guthabenAktiv = useGuthabenAktiv();
  const abgeschaltet = useMemo(
    () => new Set<NavFunktion>(guthabenAktiv ? [] : ['guthaben']),
    [guthabenAktiv],
  );
  const eintraege = useMemo(
    () => sichtbareEintraege(capabilities, bedingungen, abgeschaltet),
    [capabilities, bedingungen, abgeschaltet],
  );
  const aktiveKategorie = findeEintrag(route).kategorie?.key;
  const zustand = useSeitenspalte(alleKategorieSchluessel(), aktiveKategorie);
  const abzeichen = useAbzeichen(capabilities);
  const firmenname = useFirmenname();

  return (
    <div className="relative min-h-screen bg-background text-foreground lg:flex lg:h-screen lg:min-h-0 lg:overflow-hidden">
      <Sidebar
        eintraege={eintraege}
        aktiverPunkt={aktiverPunkt}
        aktiveKategorie={aktiveKategorie}
        zustand={zustand}
        abzeichen={abzeichen}
        user={props.user}
        navigate={props.navigation.navigate}
        firmenname={firmenname}
      />

      <div className="flex min-w-0 flex-1 flex-col lg:h-screen lg:overflow-hidden">
        <Kopfzeile
          brotkrumen={brotkrumen(route)}
          eingeklappt={zustand.eingeklappt}
          onEinklappen={zustand.einklappenUmschalten}
          farbschema={farbschema}
          hauptaktion={hauptaktionFuer(route, capabilities)}
          navigate={props.navigation.navigate}
          onSignOut={props.onSignOut}
        />

        {/* Das Hinweisband der Lizenz (`LZ-oberflaeche`): unter der Kopfzeile und
            NICHT in der Rollfläche, damit es beim Blättern stehen bleibt. Ohne
            Anlass rendert es nichts und nimmt keinen Platz. */}
        <LizenzHinweis />

        {/* `min-w-0`, sonst sprengt eine breite Tabelle den Inhaltsbereich: Ein
            Flex-Kind hat `min-width: auto` und schrumpft nicht unter seinen
            Inhalt. Der `overflow-x-auto` der Tabelle liefe damit ins Leere, und
            stattdessen bekäme die ganze Seite einen waagerechten Balken — samt
            der Menüspalte, die dann mit hinausgeschoben wird. */}
        <main className="min-w-0 flex-1 px-4 py-6 lg:overflow-y-auto lg:px-7 lg:pb-12 lg:pt-[26px]">
          {props.children}
        </main>
      </div>
    </div>
  );
}

/**
 * Die Bedingungen des Installationszustands, die einen Menüpunkt zusätzlich zur
 * Fähigkeit freigeben (`NavBedingung`) — seit `LX-webseite` (2026-10-16) eine:
 * `vorsystemAngelegt` für den Punkt «Übernahme».
 *
 * **Gefragt wird nur, wer die Fähigkeit trägt** (`vorsystem:import`, allein der
 * Administrator); für alle anderen bleibt die Menge leer und es geht keine
 * Anfrage hinaus. **Ein Fehlschlag heisst «nicht erfüllt»**, wie bei den
 * Abzeichen: kein Fehlerkasten wegen eines Menüpunkts. Neu gefragt wird, wenn
 * die Einrichtung betreten oder verlassen wird — dort entsteht die Übernahme
 * (mit dem ersten Schlüssel), und der Punkt soll danach ohne Neuladen da sein.
 *
 * **Gefragt wird nach dem einen Bit und nicht nach dem Stand** (`E-2002`): Der
 * Stand (`GET …/vorsystem/uebernahme`) kostet 16 Abfragen (gemessen), und die
 * Schale fragt ihn nur, um «angelegt ja/nein» zu wissen — bei jedem Aufbau, auch
 * bei Kunden, die nie ein Vorsystem hatten. `GET …/uebernahme/angelegt` ist eine
 * Abfrage; den Stand lädt allein die Seite «Übernahme» selbst.
 */
function useNavBedingungen(
  capabilities: ReadonlySet<string>,
  inEinrichtung: boolean,
): ReadonlySet<NavBedingung> {
  const [stand, setStand] = useState<ReadonlySet<NavBedingung>>(() => new Set<NavBedingung>());

  useEffect(() => {
    if (!capabilities.has('vorsystem:import')) {
      setStand(new Set<NavBedingung>());
      return;
    }
    let verworfen = false;
    void (async () => {
      try {
        const angelegt = await ladeUebernahmeAngelegt();
        if (verworfen) return;
        setStand(new Set<NavBedingung>(angelegt ? ['vorsystemAngelegt'] : []));
      } catch {
        // Ohne Auskunft bleibt der Punkt weg.
      }
    })();
    return () => {
      verworfen = true;
    };
  }, [capabilities, inEinrichtung]);

  return stand;
}

/**
 * Die ehrlichen Zahlen der Menüspalte — sechs seit `KP-oberflaeche`
 * (2026-09-17).
 *
 * **Genau so viele, wie es Endpunkte gibt**, die eine solche Zahl
 * liefern. Für «Bankkonten» gäbe es keine kontenübergreifende Zahl offener
 * Umsätze, für «Belege» keinen Zahlungszustand am Belegkopf; eine geschätzte
 * Zahl wäre schlimmer als keine, weil der Anwender seinen Tag danach richtet.
 *
 * **Nebeneinander und nicht hintereinander**, und jede nur, wenn die Fähigkeit
 * ihres Punktes vorliegt.
 *
 * **Ein Fehlschlag blendet das Abzeichen aus** — kein Fehlerkasten, keine Null,
 * kein Punkt. Deshalb `allSettled` und nicht `all`: Ein Rahmen, der wegen eines
 * Zählers nicht erscheint, ist die schlechteste denkbare Kopplung.
 *
 * **Gezählt wird beim Aufbau des Rahmens und nicht bei jedem Seitenwechsel.**
 * Sechs Anfragen je Navigation wären der Preis für Zahlen, die sich zwischen
 * zwei Klicks fast nie ändern; sie werden beim nächsten Laden neu geholt.
 */
function useAbzeichen(capabilities: ReadonlySet<string>): Abzeichenstand {
  const [stand, setStand] = useState<Abzeichenstand>({});

  useEffect(() => {
    let verworfen = false;
    // Der Stichtag ist heute — dieselbe Vorbelegung wie in der
    // Kreditorenerwartung selbst, damit beide Zahlen dasselbe meinen.
    const heute = new Date().toISOString().slice(0, 10);

    const auftraege: readonly (readonly [AbzeichenQuelle, string, () => Promise<number>])[] = [
      ['eingangskorb', 'incoming:read', async () => (await listIncomingDocuments(true)).total],
      ['dubletten', 'customer:read', async () => (await listDuplicates()).length],
      ['kreditorenerwartung', 'incoming:read', async () => (await getIncomingGaps(heute)).anzahl],
      // Seit `W-freigabeliste` (2026-08-27): dieselbe Liste, die der
      // Klärlisten-Abschnitt "Wartet auf Freigabe" zeigt, hier nur gezählt.
      ['kontierungWartend', 'bank:read', async () => (await listKontierungenWartend()).length],
      // Seit `WH-navabzeichen` (2026-09-10): Die Zahl der erschöpften
      // Zustellungen über alle Endpunkte, als Hinweis dass Nachsenden nötig ist.
      ['webhooksErschoepft', 'webhooks:manage', async () => await zaehleWebhookErschoepft()],
      // Seit `KP-oberflaeche` (2026-09-17): Die Zahl der offenen
      // Portal-Vorgänge — Rückfragen und Änderungsanträge aus dem
      // Kundenportal, die auf einen Menschen warten.
      ['portalVorgaengeOffen', 'document:read', async () => await zaehlePortalVorgaengeOffen()],
      // Seit `EM-web` (2026-11-04): die aktiven E-Mail-Konten, deren letzter
      // Abruf scheiterte — über die Kontenliste gezählt.
      ['maileingangFehler', 'maileingang:manage', async () => await zaehleMailKontenMitFehler()],
    ];

    void (async () => {
      const erlaubt = auftraege.filter(([, capability]) => capabilities.has(capability));
      const ergebnisse = await Promise.allSettled(erlaubt.map(async ([, , holen]) => await holen()));
      if (verworfen) return;
      const gesammelt: Record<string, number> = {};
      ergebnisse.forEach((ergebnis, index) => {
        const eintrag = erlaubt[index];
        if (eintrag === undefined || ergebnis.status !== 'fulfilled') return;
        gesammelt[eintrag[0]] = ergebnis.value;
      });
      setStand(gesammelt);
    })();

    return () => {
      verworfen = true;
    };
  }, [capabilities]);

  return stand;
}
